import { theme } from "../theme";

// Display mirror of backend/src/lib/bugReports.ts (statuses, severities,
// categories) plus the browser-side error buffer a report can carry.

export type BugStatus = "new" | "triaged" | "in_progress" | "awaiting_reporter" | "resolved" | "closed";
export type BugSeverity = "low" | "medium" | "high" | "critical";
export type BugCategory = "bug" | "display" | "data" | "performance" | "access" | "integration" | "other";

export const BUG_STATUSES: BugStatus[] = ["new", "triaged", "in_progress", "awaiting_reporter", "resolved", "closed"];
export const BUG_SEVERITIES: BugSeverity[] = ["low", "medium", "high", "critical"];

export const BUG_STATUS_LABELS: Record<BugStatus, string> = {
  new: "New",
  triaged: "Triaged",
  in_progress: "In progress",
  awaiting_reporter: "Awaiting reporter",
  resolved: "Resolved",
  closed: "Closed",
};

/** What the reporter sees: "awaiting reporter" reads as a request to them. */
export const REPORTER_STATUS_LABELS: Record<BugStatus, string> = {
  ...BUG_STATUS_LABELS,
  new: "Received",
  awaiting_reporter: "Needs your reply",
};

export const BUG_SEVERITY_LABELS: Record<BugSeverity, string> = {
  low: "Low - minor annoyance",
  medium: "Medium - something does not work",
  high: "High - blocks my work",
  critical: "Critical - data wrong or lost, or a security concern",
};

export const BUG_CATEGORY_LABELS: Record<BugCategory, string> = {
  bug: "Something does not work",
  display: "Looks wrong / layout",
  data: "Wrong or missing data",
  performance: "Slow or times out",
  access: "Sign-in or permissions",
  integration: "Connected service",
  other: "Other",
};

export function statusColours(status: BugStatus): { color: string; bg: string } {
  switch (status) {
    case "new": return { color: theme.color.accentText, bg: theme.color.accentBg };
    case "triaged":
    case "in_progress": return { color: theme.color.warningText, bg: theme.color.warningBg };
    case "awaiting_reporter": return { color: theme.color.dangerText, bg: theme.color.dangerBg };
    case "resolved": return { color: theme.color.successText, bg: theme.color.successBg };
    default: return { color: theme.color.muted, bg: theme.color.panelAlt };
  }
}

export function severityColours(severity: BugSeverity): { color: string; bg: string } {
  switch (severity) {
    case "critical":
    case "high": return { color: theme.color.dangerText, bg: theme.color.dangerBg };
    case "medium": return { color: theme.color.warningText, bg: theme.color.warningBg };
    default: return { color: theme.color.muted, bg: theme.color.panelAlt };
  }
}

export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ── Recent browser errors ─────────────────────────────────────────────────
// The last few uncaught errors and rejected promises in this tab, message text
// only (no stack, no request bodies), so a report can say what the browser saw.
// The reporter sees them in the form and can leave them out.

export interface BrowserError { at: string; message: string }

const MAX_ERRORS = 20;
const recentErrors: BrowserError[] = [];
let installed = false;

function remember(message: string) {
  const text = message.trim().slice(0, 500);
  if (!text) return;
  recentErrors.push({ at: new Date().toISOString(), message: text });
  if (recentErrors.length > MAX_ERRORS) recentErrors.splice(0, recentErrors.length - MAX_ERRORS);
}

export function installBrowserErrorBuffer() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => remember(e.message || String(e.error ?? "")));
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason as unknown;
    remember(r instanceof Error ? `${r.name}: ${r.message}` : typeof r === "string" ? r : "Unhandled promise rejection");
  });
}

export function recentBrowserErrors(): BrowserError[] {
  return recentErrors.slice();
}

installBrowserErrorBuffer();
